import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { User } from '@/lib/types'

interface ViewUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User | null
  getRoleBadgeColor: (role: string) => string
}

export function ViewUserDialog({
  open,
  onOpenChange,
  user,
  getRoleBadgeColor,
}: ViewUserDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.users.userDetails')}</DialogTitle>
        </DialogHeader>
        {user && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">{t('admin.users.email')}</Label>
                <p className="font-medium">{user.email}</p>
              </div>
              {user.roles && user.roles.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.users.role')}</Label>
                  {user.roles.map(role => (
                    <Badge key={role.id} className={getRoleBadgeColor(role.slug)}>
                      {role.name}
                    </Badge>
                  ))}
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">{t('admin.users.firstName')}</Label>
                <p className="font-medium">{user.firstName}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('admin.users.lastName')}</Label>
                <p className="font-medium">{user.lastName}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('admin.users.displayName')}</Label>
                <p className="font-medium">{user.displayName || '-'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('admin.users.status')}</Label>
                <Badge variant={user.isActive ? 'default' : 'outline'}>
                  {user.isActive ? t('admin.users.active') : t('admin.users.inactive')}
                </Badge>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('admin.users.language')}</Label>
                <p className="font-medium">{user.language}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('admin.users.timezone')}</Label>
                <p className="font-medium">{user.timezone}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('admin.users.createdAt')}</Label>
                <p className="font-medium text-sm">
                  {formatDistanceToNow(new Date(user.createdAt), {
                    addSuffix: true,
                    locale: nl,
                  })}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('admin.users.lastLogin')}</Label>
                <p className="font-medium text-sm">
                  {user.lastLoginAt
                    ? formatDistanceToNow(new Date(user.lastLoginAt), {
                        addSuffix: true,
                        locale: nl,
                      })
                    : t('admin.users.never')}
                </p>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('admin.users.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
